import isUndefined from 'lodash/isUndefined';
import omit from 'lodash/omit';
import { compose, prop, path, trim, length, mergeAll } from 'ramda';
import { createSelector } from 'reselect';

import { selectQueryParams } from '../App/selectors';

import { Feature } from './FeaturesSurveyStep/constants';
import { CompanyForm } from './ProfileStep/CompanyFormSelector/companyForm';
import { CompanySize } from './ProfileStep/CompanySizeSelector/companySize';
import {
  STEP_EMAIL,
  STEP_REGION,
  STEP_FEATURES,
  STEP_SUBMITTING,
  STEPS_ORDER,
  COUNTRIES_WITHOUT_FEATURE_SELECTION,
  STEP_PROFILE,
} from './constants';
import { ROUTE_SIGNUP_CAMPAIGN, ROUTE_SIGNUP_EXTERNAL, ROUTE_SIGNUP_PARTNER, ROUTE_SIGNUP_SANDBOX } from './routes';
import { PartnerType } from './types/partnerType';
import { sanitizeParameter } from './util';

const selectSelf = (state) => state.signup;

const selectLocation = (state) => state.location;

const selectLocationType = createSelector([selectLocation], (location) => location.type);
const selectLocationPayload = createSelector([selectLocation], (location) => location.payload);
export const selectLocationQuery = createSelector([selectLocation], (location) => location.query);

export const selectEmailParameter = createSelector([selectQueryParams], (queryParams) => queryParams?.email);
export const selectCompanyFormParameter = createSelector([selectQueryParams], (queryParams) =>
  sanitizeParameter(queryParams?.company_form, Object.values(CompanyForm)),
);
export const selectCompanySizeParameter = createSelector([selectQueryParams], (queryParams) =>
  sanitizeParameter(queryParams?.company_size, Object.values(CompanySize)),
);
export const selectSignupFeaturesParameter = createSelector([selectQueryParams], (queryParams) =>
  sanitizeParameter(queryParams?.signup_features, Object.values(Feature), true),
);

export const selectIsSandboxed = createSelector(
  [selectLocationType],
  (locationType) => locationType === ROUTE_SIGNUP_SANDBOX,
);
export const selectIsExternal = createSelector(
  [selectLocationType],
  (locationType) => locationType === ROUTE_SIGNUP_EXTERNAL,
);

export const selectPrefillStatus = createSelector([selectSelf], (state) => state.prefillStatus);

export const selectCampaign = createSelector([selectLocationPayload], (locationPayload) => locationPayload.identifier);
export const selectHasCampaign = createSelector(
  [selectLocationType, selectLocationPayload],
  (locationType, locationPayload) => locationType === ROUTE_SIGNUP_CAMPAIGN && !isUndefined(locationPayload.identifier),
);

export const selectPartner = createSelector([selectLocationPayload], (locationPayload) => {
  const type = locationPayload.type;
  const id = locationPayload.identifier;

  if (!isUndefined(id) && !isNaN(parseInt(id)) && Object.values(PartnerType).includes(type)) {
    return { type, id: parseInt(id) };
  }
  return undefined;
});
export const selectHasPartner = createSelector(
  [selectLocationType, selectPartner],
  (locationType, partner) => locationType === ROUTE_SIGNUP_PARTNER && !isUndefined(partner),
);

export const selectHasEInvoicingEnabled = path(['signup', 'eInvoicingEnabled']);

export const selectCountry = createSelector(
  [selectSelf, selectHasEInvoicingEnabled],
  (signup, hasEInvoicingEnabled) => {
    if (hasEInvoicingEnabled) {
      return signup.steps[STEP_PROFILE].country;
    }

    return signup.steps[STEP_REGION].country;
  },
);

export const selectHasEInvoicingAvailable = createSelector(
  [selectCountry, selectHasEInvoicingEnabled, selectIsSandboxed],
  (country, hasEInvoicingEnabled, isSandboxed) => country === 'BE' && hasEInvoicingEnabled && !isSandboxed,
);

export const selectShouldSkipFeaturesStepBasedOnSelectedCountry = createSelector([selectCountry], (country) => {
  return COUNTRIES_WITHOUT_FEATURE_SELECTION.includes(country);
});

export const selectShouldSkipFeaturesStep = createSelector(
  [
    selectIsExternal,
    selectHasPartner,
    selectSignupFeaturesParameter,
    selectShouldSkipFeaturesStepBasedOnSelectedCountry,
    selectHasEInvoicingEnabled,
  ],
  (
    isExternal,
    hasPartner,
    signupFeaturesParameter,
    shouldSkipFeaturesStepBasedOnSelectedCountry,
    hasEInvoicingEnabled,
  ) => {
    if (shouldSkipFeaturesStepBasedOnSelectedCountry || hasEInvoicingEnabled) {
      return true;
    }
    return (isExternal || hasPartner) && !isUndefined(signupFeaturesParameter);
  },
);

export const selectEmailValidation = (state) => state.signup.emailValidation;

export const selectSignupFailed = path(['signup', 'signupFailed']);

export const selectSteps = path(['signup', 'steps']);

export const selectFormValues = createSelector(
  [selectSteps, selectHasEInvoicingEnabled],
  (steps, hasEInvoicingEnabled) => {
    if (hasEInvoicingEnabled) {
      const sanitizedSteps = omit(steps, `${STEP_REGION}.country`);
      return mergeAll(Object.values(sanitizedSteps));
    }

    const sanitizedSteps = omit(steps, `${STEP_PROFILE}.country`);
    return mergeAll(Object.values(sanitizedSteps));
  },
);

export const selectStepsOrder = () => STEPS_ORDER;

export const selectStepIndex = path(['signup', 'stepIndex']);

export const selectVisibleSteps = createSelector(
  [selectShouldSkipFeaturesStep, selectStepsOrder],
  (shouldSkipFeaturesStep, orderedSteps) => {
    return orderedSteps.filter((stepName) => {
      switch (stepName) {
        case STEP_FEATURES:
          return !shouldSkipFeaturesStep;
        default:
          return true;
      }
    });
  },
);

export const selectStepIndicatorSteps = createSelector([selectVisibleSteps], (visibleSteps) => {
  const steps = visibleSteps.filter((stepName) => {
    switch (stepName) {
      case STEP_SUBMITTING:
        return false;
      default:
        return true;
    }
  });

  return steps.slice(1);
});

export const selectStepIndicatorIndex = createSelector(
  [selectStepIndex, selectStepsOrder, selectVisibleSteps],
  (stepIndex, stepsOrder, visibleSteps) => {
    const stepName = stepsOrder[stepIndex];

    return visibleSteps.indexOf(stepName);
  },
);

export const selectTotalSteps = createSelector(selectStepIndicatorSteps, length);

export const selectMaxStepIndexSeen = path(['signup', 'maxStepIndexSeen']);

export const selectIsSigningUp = createSelector(
  selectStepIndex,
  selectStepsOrder,
  (stepIndex, stepsOrder) => stepIndex === stepsOrder.indexOf(STEP_SUBMITTING),
);

export const selectEmail = createSelector(selectSteps, compose(trim, prop('email'), prop(STEP_EMAIL)));

export const selectCurrentStep = createSelector(
  selectStepIndex,
  selectStepsOrder,
  selectSteps,
  selectHasEInvoicingEnabled,
  (stepIndex, stepsOrder, stepsState, hasEInvoicingEnabled) => {
    const stepName = stepsOrder[stepIndex];

    if (hasEInvoicingEnabled) {
      if (stepName === STEP_REGION) {
        return omit(stepsState[stepName], ['companyForm', 'country']);
      }

      return omit(stepsState[stepName], ['companyForm']);
    }

    if (!hasEInvoicingEnabled) {
      if (stepName === STEP_PROFILE) {
        return omit(stepsState[stepName], ['companyLookup', 'country']);
      }

      return omit(stepsState[stepName], ['companyLookup']);
    }
  },
);

export const selectReferralToken = path(['signup', 'referralToken']);

export const selectIdentityProviderToken = path(['signup', 'identityProvider', 'token']);

export const selectIdentityProviderError = path(['signup', 'identityProvider', 'error']);

export const selectSignupFeatures = path(['signup', 'steps', STEP_FEATURES, 'signupFeatures']);

export const selectLanguage = path(['signup', 'steps', STEP_REGION, 'language']);

export const selectCompanyLookupLoading = path(['signup', 'companyLookup', 'isLoading']);

export const selectCompanyLookupResults = path(['signup', 'companyLookup', 'results']);
